/**
 * Pluggable, always-encrypted persistence.
 *
 * The engine never sees plaintext storage: it hands records to a Vault,
 * and the Vault hands sealed envelopes to whichever backend this runtime
 * has - IndexedDB in a browser tab, the filesystem on a server, Durable
 * Object storage on Cloudflare. Swapping the backend cannot accidentally
 * un-encrypt the replica, because the encryption is on this side of the
 * interface.
 */

import { Vault, type SealedRecord } from './vault.ts';
export type { SealedRecord };
import type { SignedVertex, VertexId } from './tx.ts';

export interface KvStore {
  get(key: string): Promise<SealedRecord | undefined>;
  put(key: string, value: SealedRecord): Promise<void>;
  delete(key: string): Promise<void>;
  list(prefix: string, limit?: number): Promise<{ key: string; value: SealedRecord }[]>;
  close?(): Promise<void>;
}

export class MemoryKv implements KvStore {
  private m = new Map<string, SealedRecord>();
  async get(key: string) { return this.m.get(key); }
  async put(key: string, value: SealedRecord) { this.m.set(key, value); }
  async delete(key: string) { this.m.delete(key); }
  async list(prefix: string, limit = 1000) {
    const out: { key: string; value: SealedRecord }[] = [];
    for (const [k, v] of this.m) {
      if (!k.startsWith(prefix)) continue;
      out.push({ key: k, value: v });
      if (out.length >= limit) break;
    }
    out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
  }
}

/** Sequence-ordered, encrypted vertex log. Replay reconstructs the DAG. */
export class LedgerStore {
  private kv: KvStore;
  private vault: Vault;
  private seq = 0;

  constructor(kv: KvStore, seed: Uint8Array, scope = 'ledger') {
    this.kv = kv;
    this.vault = new Vault(seed, scope);
  }

  private static seqKey(n: number): string {
    return 'v/' + n.toString(36).padStart(10, '0');
  }

  get height(): number {
    return this.seq;
  }

  async append(v: SignedVertex): Promise<number> {
    const n = this.seq++;
    const key = LedgerStore.seqKey(n);
    await this.kv.put(key, this.vault.sealJson(key, v));
    await this.kv.put('idx/' + v.id, this.vault.sealJson('idx/' + v.id, { seq: n }));
    await this.kv.put('meta/height', this.vault.sealJson('meta/height', { seq: this.seq }));
    return n;
  }

  async byId(id: VertexId): Promise<SignedVertex | undefined> {
    const rec = await this.kv.get('idx/' + id);
    if (!rec) return undefined;
    const { seq } = this.vault.openJson<{ seq: number }>('idx/' + id, rec);
    return this.at(seq);
  }

  async at(seq: number): Promise<SignedVertex | undefined> {
    const key = LedgerStore.seqKey(seq);
    const rec = await this.kv.get(key);
    if (!rec) return undefined;
    try {
      return this.vault.openJson<SignedVertex>(key, rec);
    } catch {
      // Sealed under a different key, or damaged. Ledger data is public
      // and re-syncable, so an unreadable record is a gap to refill from
      // peers, never a reason to refuse to start.
      return undefined;
    }
  }

  async range(from: number, limit: number): Promise<SignedVertex[]> {
    const out: SignedVertex[] = [];
    for (let i = from; i < from + limit && i < this.seq; i++) {
      const v = await this.at(i);
      if (v) out.push(v);
    }
    return out;
  }

  /**
   * Rebuild the sequence counter after a restart. If the replica turns out
   * to be sealed under a key this node no longer has - a rotated device
   * key, a restored backup, a shared browser profile - it is discarded
   * rather than half-read. Nothing is lost: every vertex is public, signed
   * and re-fetchable from any peer.
   */
  async load(): Promise<number> {
    const rec = await this.kv.get('meta/height');
    if (!rec) {
      const rows = await this.kv.list('v/', 1_000_000);
      this.seq = rows.length;
      return this.seq;
    }
    try {
      this.seq = this.vault.openJson<{ seq: number }>('meta/height', rec).seq;
      return this.seq;
    } catch {
      await this.reset();
      return 0;
    }
  }

  /** Drop the local replica. The network is the backup. */
  async reset(): Promise<void> {
    for (const prefix of ['v/', 'idx/', 'meta/']) {
      for (const { key } of await this.kv.list(prefix, 1_000_000)) await this.kv.delete(key);
    }
    this.seq = 0;
  }

  async replay(onVertex: (v: SignedVertex) => void | Promise<void>): Promise<number> {
    let n = 0;
    for (let i = 0; i < this.seq; i++) {
      const v = await this.at(i);
      if (!v) continue;
      await onVertex(v);
      n++;
    }
    return n;
  }
}
